import { Box, Typography, Select, MenuItem, FormControl, TextField, type SxProps, type Theme } from '@mui/material';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';
import React, { useState, useEffect, useRef, HTMLAttributes, JSXElementConstructor } from 'react';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { LoadingOverlay, NoResultsFound, NoRowsOverlay } from './TableOverlay';
import { GridColumnHeader, CustomImage, PillCell } from './TableElements/TableElements';
import { MoreHoriz } from '@mui/icons-material';
import { formatDate, useDispatchPromise } from '../../utils/AppUtils';
import { StatusBadge } from './StatusBadge';
import { RoleSelector } from './RoleSelector';
import { TDashboardCategory } from '../../redux/user/types';
import _, { isEmpty } from 'lodash';
import { getMyPlaylist } from '../../redux/project/actions';
import { getUsersDashboard, updateUser } from '../../redux/user/actions';
import { getSavedTalents } from '../../redux/talent/actions';
import { CustomPagination } from './Pagination';
import { TMoreMenuItem } from '../More/more.data';
import { FullScreenMoreMenu, IMore } from '../More/FullScreenMoreMenu';
import { DashboardGlassTableBody } from './DashboardGlassTableBody';
import { ObsidianDashboardTableBody } from '../Dashboard/obsidian/ObsidianDashboardTableBody';
import { ObsidianDashboardSearchField } from '../Dashboard/obsidian/ObsidianDashboardSearchField';
import {
    ObsidianDashboardFilterSelect,
    ObsidianFilterMenuItem,
} from '../Dashboard/obsidian/ObsidianDashboardFilterSelect';
import { ObsidianDashboardTablePagination } from '../Dashboard/obsidian/ObsidianDashboardTablePagination';
import { ensureObsidianDashboardFonts } from '../../constants/obsidianDashboardTheme';

const getFieldValue = (params: any, field: string) => _.get(params.row, field);

export interface ColumnsData {
    /**
     * @value field should be set to the object property for type's text, status, or dropdown
     * @value field should be set to 'image' for type image */
    field: any;
    /**header label/text */
    label: string;
    /**
     * @value set customCell property if type is custom
     * @value set textType property if type is text
     */
    type: 'text' | 'image' | 'action' | 'custom' | 'active' | 'dropdown' | 'status' | 'pill';

    /**some text values are in date format or number format so this property handles that possibility */
    textType?: 'date' | 'number' | 'regular';

    width?: number | string;
    flex?: number;
    /**function that takes in params and returns custom React Element */
    customCell?: (params: any, index?: number, moreArgs?: any) => React.ReactElement;
    alignColumn?: 'center' | 'left' | 'right';

    headerIcon?: React.ReactElement;

    /**set sortable state*/
    sortable: boolean;
    onClick?: (params: any) => void;
}

interface slots {
    noResultsOverlay?: JSXElementConstructor<HTMLAttributes<HTMLDivElement>>;
    loadingOverlay?: JSXElementConstructor<HTMLAttributes<HTMLDivElement>>;
    noRowsOverlay?: JSXElementConstructor<HTMLAttributes<HTMLDivElement>>;
}

interface ITable {
    /**column data that is going to be used to populate the table */
    columns?: ColumnsData[];
    /**type of table that is going to be populated */
    tableType:
        | 'admin'
        | 'admin-search'
        | 'user'
        | 'user-search'
        | 'playlist'
        | 'project'
        | 'campaigns'
        | 'campaign'
        | 'talent'
        | 'other'
        | 'gigs'
        | 'application'
        | 'events'
        | 'tickets'
        | 'sales'
        | 'submissions'
        | 'requests';
    /**an array of numbers for the different options that the table will have to set rows per page */
    rowsPerPageOptions: number[];
    /**set to the data that should be displayed if pagination is unavailable */
    staticData?: any[];
    /**used to set the row height for every row */
    rowHeight?: number;
    /**used to set the header height */
    headerHeight?: number;
    /**use if you need custom slots for the table */
    slots?: slots;
    /**set imageType if type property is set to image,
     * a custom image cell is required if image type is set to other
     */
    imageType?: 'profile' | 'project' | 'playlist' | 'campaign' | 'talent' | 'submissions' | 'other' | 'requests';
    /**used to style the table */
    sx?: SxProps<Theme>;
    /**used to style the text */
    textSx?: SxProps<Theme>;
    /**generate a custom table button */
    customTableButton?: React.ReactElement;
    /**loading overlay toggled based on api call outside the table */
    externalLoading?: boolean;
    /**used to fetch table data outside of the component */
    getTableData?: (payload: any) => any;
    /**table action to perform for the row on click */
    moreActions?: (param: any, index?: number) => any;
    /**useful for only paginating without a table as long as the api call is made inside the table */
    paginateOnly?: boolean;
    /**handles external loading state */
    handleLoad?: (param: boolean) => void;
    /**gets the category of the dashboard to display */
    dashboardCategory?: TDashboardCategory;
    /**some extra payload the table can take */
    extraPayload?: object;

    /** Custom More Icon */
    fullscreenMoreIcon?: boolean;
    fullscreenMoreMenu?: TMoreMenuItem[] | ((row: any) => TMoreMenuItem[]);
    fullscreenMoreSx?: object;
    onFullscreenMenuClick?: (type: string, payload: any) => void;
    moreMenuProps?: IMore;
    onActionMenuButtonClicked?: (paylaod: any) => void;
    /** Glass / list row click (e.g. open submission detail) */
    onRowClick?: (row: any) => void;
    /** Glass row list (dashboard) instead of MUI DataGrid */
    layoutVariant?: 'dataGrid' | 'glass' | 'obsidian';
}

export const Table: React.FC<ITable> = ({
    columns,
    tableType,
    rowsPerPageOptions,
    staticData,
    slots,
    sx,
    textSx,
    rowHeight,
    headerHeight,
    imageType,
    customTableButton,
    externalLoading,
    paginateOnly,
    dashboardCategory,
    extraPayload,
    getTableData,
    moreActions,
    handleLoad,
    fullscreenMoreIcon,
    fullscreenMoreMenu,
    fullscreenMoreSx,
    onFullscreenMenuClick,
    moreMenuProps,
    onActionMenuButtonClicked,
    onRowClick,
    layoutVariant = 'dataGrid',
}) => {
    const apiRef = useGridApiRef();
    const { themeStyle, device } = useThemeContext();
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [tableColumns, setTableColumns] = useState<any>([]);
    const [data, setData] = useState<any>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [searchLoading, setSearchLoading] = useState<boolean>(false);
    const [page, setPage] = useState<number>(1);
    const [rowsPerPage, setRowsPerPage] = useState<number>(rowsPerPageOptions[0]);
    const [count, setCount] = useState<number>(0);
    const [sortBy, setSortBy] = useState<string>('none');
    const [sort, setSort] = useState<-1 | 1>(1);
    const dispatch = useDispatchPromise();

    useEffect(() => {
        /**
         * The below is NOT ideal and we shoudl refactor it
         * It handles user tables but also has a use case in other places
         * which will lead to conflict in the types.
         */
        let processedColumns = columns ? [...columns] : [];
        // If no column has flex, set flex: 1 on the last column
        if (processedColumns.length > 0 && !processedColumns.some(col => col.flex)) {
            processedColumns[processedColumns.length - 1] = {
                ...processedColumns[processedColumns.length - 1],
                // flex: 1,
            };
        }
        const createdColumns =
            !paginateOnly &&
            processedColumns &&
            processedColumns.map(column => {
                switch (column.type) {
                    case 'text':
                        return {
                            field: column.field,
                            ...(column.width && { width: column.width }),
                            ...(column.alignColumn && {
                                align: column.alignColumn,
                                headerAlign: column.alignColumn,
                            }),
                            sortable: column.sortable,
                            ...(column.flex && column.flex > 0 && { flex: column.flex }),
                            display: 'flex',
                            renderHeader: () => (
                                <GridColumnHeader icon={column.headerIcon} label={column.label} textSx={textSx} />
                            ),
                            renderCell: (params: any) => {
                                return (
                                    <Typography
                                        onClick={() => column.onClick && column.onClick(params.row)}
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            fontSize: 12,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            color: theme[themeStyle].tag_white,
                                            cursor: column.onClick ? 'pointer' : 'none',
                                        }}>
                                        {column.textType && column.textType === 'date'
                                            ? formatDate(getFieldValue(params, column.field), 'MMM D, YY [at] h:mm A')
                                            : getFieldValue(params, column.field)}
                                    </Typography>
                                );
                            },
                        };
                    case 'image':
                        return {
                            field: column.field,
                            ...(column.width && { width: column.width }),
                            ...(column.alignColumn && {
                                align: column.alignColumn,
                                headerAlign: column.alignColumn,
                            }),
                            sortable: column.sortable,
                            ...(column.flex && column.flex > 0 && { flex: column.flex }),
                            display: 'flex',
                            renderHeader: () => (
                                <GridColumnHeader icon={column.headerIcon} label={column.label} textSx={textSx} />
                            ),
                            renderCell: (params: any) => {
                                return (
                                    <CustomImage
                                        device={device}
                                        imageType={imageType}
                                        params={params}
                                        field={column.field}
                                        onClick={() => column.onClick && column.onClick(params.row)}
                                    />
                                );
                            },
                        };
                    case 'action':
                        return {
                            field: column.field,
                            ...(column.width && { width: column.width }),
                            ...(column.alignColumn && {
                                align: column.alignColumn,
                                headerAlign: column.alignColumn,
                            }),
                            sortable: column.sortable,
                            ...(column.flex && column.flex > 0 && { flex: column.flex }),
                            display: 'flex',
                            renderHeader: () => (
                                <GridColumnHeader icon={column.headerIcon} label={column.label} textSx={textSx} />
                            ),
                            renderCell: (params: any) => {
                                if (fullscreenMoreIcon && fullscreenMoreMenu && onFullscreenMenuClick) {
                                    // Support both array and function for dynamic menu items
                                    const menuItems =
                                        typeof fullscreenMoreMenu === 'function'
                                            ? fullscreenMoreMenu(params.row)
                                            : fullscreenMoreMenu;

                                    return (
                                        <FullScreenMoreMenu
                                            menu={menuItems}
                                            onClick={moreType => onFullscreenMenuClick(moreType, params.row)}
                                            onIconClick={() =>
                                                onActionMenuButtonClicked && onActionMenuButtonClicked(params.row)
                                            }
                                            {...moreMenuProps}
                                            customMoreIcon={
                                                <MoreHoriz
                                                    sx={{
                                                        transform: 'rotate(180deg)',
                                                        color: theme[themeStyle].tag_white,
                                                        ...fullscreenMoreSx,
                                                    }}
                                                />
                                            }
                                        />
                                    );
                                }
                                return (
                                    <MoreHoriz
                                        onClick={() =>
                                            moreActions &&
                                            moreActions(params.row, params.api.getAllRowIds().indexOf(params.id))
                                        }
                                        sx={{
                                            color: theme[themeStyle].tag_white,
                                            cursor: 'pointer',
                                        }}
                                    />
                                );
                            },
                        };
                    case 'active':
                    case 'status':
                        return {
                            field: column.field,
                            ...(column.width && { width: column.width }),
                            ...(column.alignColumn && {
                                align: column.alignColumn,
                                headerAlign: column.alignColumn,
                            }),
                            sortable: column.sortable,
                            ...(column.flex && column.flex > 0 && { flex: column.flex }),
                            display: 'flex',
                            renderHeader: () => (
                                <GridColumnHeader icon={column.headerIcon} label={column.label} textSx={textSx} />
                            ),
                            renderCell: (params: any) =>
                                column.type === 'active' ? (
                                    <StatusBadge statusType="admin-user" active={params.row.active ?? false} />
                                ) : (
                                    <StatusBadge
                                        statusType="creative"
                                        projectStatus={getFieldValue(params, column.field)}
                                    />
                                ),
                        };
                    case 'dropdown':
                        return {
                            field: column.field,
                            ...(column.width && { width: column.width }),
                            ...(column.alignColumn && {
                                align: column.alignColumn,
                                headerAlign: column.alignColumn,
                            }),
                            sortable: column.sortable,
                            ...(column.flex && column.flex > 0 && { flex: column.flex }),
                            display: 'flex',
                            renderHeader: () => (
                                <GridColumnHeader icon={column.headerIcon} label={column.label} textSx={textSx} />
                            ),
                            // Fine ways to redo this
                            renderCell: (params: any) => (
                                <RoleSelector
                                    selectedRole={getFieldValue(params, column.field)}
                                    onRoleChange={event => {}}
                                    onRoleConfirm={role => {
                                        handleUpdateUser(role, params.row._id);
                                    }}
                                    variant="standard"
                                    shouldUnderline
                                    selectSx={{
                                        color: theme[themeStyle].tag_white,
                                        borderBottomColor: theme[themeStyle].tag_white,
                                        '&:hover': {
                                            borderBottomWidth: '2px',
                                        },
                                    }}
                                    selectIconSx={{
                                        color: theme[themeStyle].tag_white,
                                    }}
                                    confirmSx={{
                                        gap: 0,
                                    }}
                                />
                            ),
                        };
                    case 'pill':
                        return {
                            field: column.field,
                            ...(column.width && { width: column.width }),
                            ...(column.alignColumn && {
                                align: column.alignColumn,
                                headerAlign: column.alignColumn,
                            }),
                            sortable: column.sortable,
                            ...(column.flex && column.flex > 0 && { flex: column.flex }),
                            display: 'flex',
                            renderHeader: () => (
                                <GridColumnHeader icon={column.headerIcon} label={column.label} textSx={textSx} />
                            ),
                            renderCell: (params: any) => (
                                <PillCell label={getFieldValue(params, column.field)} textSx={textSx} />
                            ),
                        };
                    default:
                        return {
                            field: column.field,
                            ...(column.width && { width: column.width }),
                            ...(column.alignColumn && {
                                align: column.alignColumn,
                                headerAlign: column.alignColumn,
                            }),
                            sortable: column.sortable,
                            ...(column.flex && column.flex > 0 && { flex: column.flex }),
                            display: 'flex',
                            renderHeader: () => (
                                <GridColumnHeader icon={column.headerIcon} label={column.label} textSx={textSx} />
                            ),
                            renderCell: (params: any) => {
                                const value = getFieldValue(params, column.field);
                                return (
                                    column.customCell &&
                                    column.customCell(params.row, params.api.getAllRowIds().indexOf(params.id), value)
                                );
                            },
                        };
                }
            });
        setTableColumns(createdColumns);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        columns,
        moreActions,
        themeStyle,
        imageType,
        device,
        textSx,
        paginateOnly,
        onFullscreenMenuClick,
        fullscreenMoreIcon,
        fullscreenMoreMenu,
        moreMenuProps,
        tableType,
        fullscreenMoreSx,
    ]);

    const handlePageChange = (page: number) => {
        setPage(page);
    };

    const handleRowsChange = (rows: number) => {
        setRowsPerPage(rows);
        setPage(1);
    };

    const filteredData = React.useMemo(() => {
        return data;
    }, [data]);

    const handleFetchData = async () => {
        let getType;

        switch (tableType) {
            case 'playlist':
                getType = getMyPlaylist;
                break;
            case 'talent':
                getType = getSavedTalents;
                break;
        }

        switch (tableType) {
            case 'admin-search':
            case 'admin':
                const response: any = await dispatch(
                    getUsersDashboard({
                        category: dashboardCategory || 'users',
                        page: page,
                        limit: rowsPerPage,
                        skipRefresh: true,
                        replaceData: true,
                        searchTerm: searchTerm,
                        ...(sortBy !== 'none' && { sortBy, sort }),
                    }),
                );
                const { data, currentPage, pageSize, totalCount } = response[dashboardCategory || 'users'];
                setData(data);
                setCount(totalCount ?? count);
                setRowsPerPage(pageSize ?? rowsPerPage);
                setPage(Number(currentPage) ?? page);
                setLoading(false);
                break;
            case 'campaigns':
                const campaignResponse = await dispatch(
                    getUsersDashboard({
                        page: page,
                        limit: rowsPerPage,
                        category: tableType,
                        skipRefresh: true,
                    }),
                );
                const {
                    [tableType]: {
                        data: campaignDate,
                        currentPage: camapignCurrentPage,
                        pageSize: campaignPageSize,
                        totalCount: campaignTotalCount,
                    },
                }: any = campaignResponse;
                setData(campaignDate);
                setCount(campaignTotalCount ?? count);
                setRowsPerPage(+campaignPageSize || rowsPerPage);
                setPage(+camapignCurrentPage || page);
                setLoading(false);
                break;
            case 'playlist':
            case 'talent':
                const responseOther = await dispatch(
                    getType &&
                        getType({
                            page: page,
                            limit: rowsPerPage,
                        }),
                );
                const {
                    data: { data: dataOther },
                    currentPage: currentPageOther,
                    pageSize: pageSizeOther,
                    totalCount: totalCountOther,
                }: any = responseOther;
                setData(dataOther);
                setCount(totalCountOther ?? count);
                setRowsPerPage(+pageSizeOther || rowsPerPage);
                setPage(+currentPageOther || page);
                setLoading(false);
                break;
            case 'application':
                const applicationResponse = await dispatch(
                    getUsersDashboard({
                        page: page,
                        limit: rowsPerPage,
                        category: tableType,
                        skipRefresh: true,
                    }),
                );
                const {
                    [tableType]: {
                        data: applicatiionData,
                        currentPage: applicationCurrentPage,
                        pageSize: applicationPageSize,
                        totalCount: applicationTotalCount,
                    },
                }: any = applicationResponse;
                setData(applicatiionData);
                setCount(applicationTotalCount ?? count);
                setRowsPerPage(+applicationPageSize || rowsPerPage);
                setPage(+applicationCurrentPage || page);
                setLoading(false);
                break;
            case 'gigs':
                const auditionResponse = await dispatch(
                    getUsersDashboard({
                        page: page,
                        limit: rowsPerPage,
                        category: tableType,
                        skipRefresh: true,
                    }),
                );
                const {
                    [tableType]: {
                        data: dashbaordData,
                        currentPage: dashboardCurrentPage,
                        pageSize: dashboardPageSize,
                        totalCount: dashboardTotalCount,
                    },
                }: any = auditionResponse;
                setData(dashbaordData);
                setCount(dashboardTotalCount ?? count);
                setRowsPerPage(+dashboardPageSize || rowsPerPage);
                setPage(+dashboardCurrentPage || page);
                setLoading(false);
                break;
            case 'events':
                const eventResponse = await dispatch(
                    getUsersDashboard({
                        page: page,
                        limit: rowsPerPage,
                        category: tableType,
                        skipRefresh: true,
                    }),
                );
                const {
                    [tableType]: {
                        data: eventDatadData,
                        currentPage: eventCurrentPage,
                        pageSize: eventPageSize,
                        totalCount: eventTotalCount,
                    },
                }: any = eventResponse;
                setData(eventDatadData);
                setCount(eventTotalCount ?? count);
                setRowsPerPage(+eventPageSize || rowsPerPage);
                setPage(+eventCurrentPage || page);
                setLoading(false);
                break;
            case 'tickets':
                const ticketResponse = await dispatch(
                    getUsersDashboard({
                        page: page,
                        limit: rowsPerPage,
                        category: tableType,
                        skipRefresh: true,
                    }),
                );
                const {
                    [tableType]: {
                        data: ticketDatadData,
                        currentPage: ticketCurrentPage,
                        pageSize: ticketPageSize,
                        totalCount: ticketTotalCount,
                    },
                }: any = ticketResponse;
                setData(ticketDatadData);
                setCount(ticketTotalCount ?? count);
                setRowsPerPage(+ticketPageSize || rowsPerPage);
                setPage(+ticketCurrentPage || page);
                setLoading(false);
                break;
            case 'requests':
                const requestResponse = await dispatch(
                    getUsersDashboard({
                        page: page,
                        limit: rowsPerPage,
                        category: tableType,
                        skipRefresh: true,
                    }),
                );
                const {
                    [tableType]: {
                        data: requestDatadData,
                        currentPage: requestCurrentPage,
                        pageSize: requestPageSize,
                        totalCount: requestTotalCount,
                    },
                }: any = requestResponse;
                setData(requestDatadData);
                setCount(requestTotalCount ?? count);
                setRowsPerPage(+requestPageSize || rowsPerPage);
                setPage(+requestCurrentPage || page);
                setLoading(false);
                break;
            case 'other':
                const responseUnique = await dispatch(
                    getTableData &&
                        getTableData({
                            page: page,
                            limit: rowsPerPage,
                            ...extraPayload,
                        }),
                );
                const {
                    data: { data: dataUnique },
                    currentPage: currentPageUnique,
                    pageSize: pageSizeUnique,
                    totalCount: totalCountUnique,
                }: any = responseUnique;
                setData(dataUnique);
                setCount(totalCountUnique ?? count);
                setRowsPerPage(+pageSizeUnique || rowsPerPage);
                setPage(+currentPageUnique || page);
                setLoading(false);
                break;
            case 'submissions':
                const submissionReponse = await dispatch(
                    getUsersDashboard({
                        page: page,
                        limit: rowsPerPage,
                        category: tableType,
                        skipRefresh: true,
                    }),
                );

                const {
                    [tableType]: {
                        data: submissionData,
                        currentPage: submissionPage,
                        pageSize: submissionPageSize,
                        totalCount: submissionTotalCount,
                    },
                }: any = submissionReponse;
                setData(submissionData);
                setCount(submissionTotalCount ?? count);
                setRowsPerPage(+submissionPageSize || rowsPerPage);
                setPage(+submissionPage || page);
                setLoading(false);
                break;
        }
    };

    const handleUpdateUser = (role: string, userId: string) => {
        dispatch(
            updateUser({
                role,
                userId,
            }),
        );
    };

    useEffect(() => {
        const getData = async () => {
            setLoading(true);
            handleLoad && handleLoad(true);
            try {
                await handleFetchData();
            } catch (error) {
                console.log('Error Fetching data', error);
            } finally {
                setLoading(false);
                handleLoad && handleLoad(false);
            }
        };
        if (!staticData) getData();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage]);

    /**search functionality only for admins at this time */
    const handleDebouncedSearch = useRef(
        _.debounce(async (searchTerm: string) => {
            try {
                if (searchTerm.length > 0) {
                    setSearchLoading(true);
                    const response: any = await dispatch(
                        getUsersDashboard({
                            category: dashboardCategory || 'users',
                            page: 1,
                            limit: rowsPerPage,
                            skipRefresh: true,
                            searchTerm: searchTerm,
                            ...(sortBy !== 'none' && { sortBy, sort }),
                        }),
                    );
                    const { data, totalCount } = response[dashboardCategory || 'users'];
                    setData(data);
                    setCount(totalCount!);
                    setPage(1);
                } else {
                    setSearchLoading(true);
                    await handleFetchData();
                }
            } catch (error) {
                console.log('Error Fetching data');
            } finally {
                setSearchLoading(false);
            }
        }, 500),
    ).current;

    /**search functionality only for admins at this time */
    const handleSearch = async (text: string) => {
        setSearchTerm(text);
        handleDebouncedSearch(text);
    };

    const handleSortChange = async (newSortBy: string) => {
        setSortBy(newSortBy);
        if (newSortBy === 'none') {
            // Reset to default fetch without sort
            setPage(1);
            setLoading(true);
            try {
                const response: any = await dispatch(
                    getUsersDashboard({
                        category: dashboardCategory || 'users',
                        page: 1,
                        limit: rowsPerPage,
                        skipRefresh: true,
                        searchTerm: searchTerm,
                    }),
                );
                const { data, totalCount } = response[dashboardCategory || 'users'];
                setData(data);
                setCount(totalCount!);
                setPage(1);
            } catch (error) {
                console.log('Error Fetching data');
            } finally {
                setLoading(false);
            }
        } else {
            // Refetch with new sort
            setPage(1);
            setLoading(true);
            try {
                const response: any = await dispatch(
                    getUsersDashboard({
                        category: dashboardCategory || 'users',
                        page: 1,
                        limit: rowsPerPage,
                        skipRefresh: true,
                        searchTerm: searchTerm,
                        sortBy: newSortBy,
                        sort: sort,
                    }),
                );
                const { data, totalCount } = response[dashboardCategory || 'users'];
                setData(data);
                setCount(totalCount!);
                setPage(1);
            } catch (error) {
                console.log('Error Fetching data');
            } finally {
                setLoading(false);
            }
        }
    };

    const handleSortDirectionChange = async (newSort: -1 | 1) => {
        setSort(newSort);
        if (sortBy !== 'none') {
            // Refetch with new sort direction
            setPage(1);
            setLoading(true);
            try {
                const response: any = await dispatch(
                    getUsersDashboard({
                        category: dashboardCategory || 'users',
                        page: 1,
                        limit: rowsPerPage,
                        skipRefresh: true,
                        searchTerm: searchTerm,
                        sortBy: sortBy,
                        sort: newSort,
                    }),
                );
                const { data, totalCount } = response[dashboardCategory || 'users'];
                setData(data);
                setCount(totalCount!);
                setPage(1);
            } catch (error) {
                console.log('Error Fetching data');
            } finally {
                setLoading(false);
            }
        }
    };

    // Clean up the debounced function on unmount
    useEffect(() => {
        return () => {
            handleDebouncedSearch.cancel();
        };
    }, [handleDebouncedSearch]);

    // Combined loading state - only impacts the data grid
    const isTableLoading = loading || externalLoading;

    // Search-only loading doesn't affect the whole table UI
    const isSearchingResults = searchLoading && !isTableLoading;

    const useObsidianLayout = layoutVariant === 'obsidian';
    const useGlassLayout = layoutVariant === 'glass';

    useEffect(() => {
        if (useObsidianLayout) {
            ensureObsidianDashboardFonts();
        }
    }, [useObsidianLayout]);

    const renderDashboardListBody = () => {
        const bodyProps = {
            tableColumns,
            rows: !staticData ? filteredData : staticData || [],
            isLoading: isTableLoading ?? false,
            headerHeight,
            rowMinHeight: rowHeight,
            textSx,
            emptyOverlay: slots?.noRowsOverlay,
            dimmed: isSearchingResults,
        };
        if (useObsidianLayout) {
            return <ObsidianDashboardTableBody {...bodyProps} />;
        }
        if (useGlassLayout) {
            return <DashboardGlassTableBody {...bodyProps} onRowClick={onRowClick} />;
        }
        return null;
    };

    const renderDashboardPagination = () => {
        if (staticData) return null;
        if (useObsidianLayout) {
            return (
                <ObsidianDashboardTablePagination
                    page={page}
                    rowsPerPage={rowsPerPage}
                    totalCount={count}
                    rowsPerPageOptions={rowsPerPageOptions}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsChange}
                />
            );
        }
        return (
            <CustomPagination
                pagination={{
                    page: page,
                    rows: rowsPerPage,
                    totalCount: count,
                }}
                rowsPerPageOptions={rowsPerPageOptions}
                onPageChange={handlePageChange}
                onRowsChange={handleRowsChange}
            />
        );
    };

    // Custom overlay for search operations
    const SearchingOverlay = () => {
        if (!isSearchingResults) return null;

        return (
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 5,
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    borderRadius: '9px',
                    pointerEvents: 'none', // Allow clicks to pass through
                }}>
                <Typography
                    sx={{
                        color: theme[themeStyle].tag_white,
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        fontSize: '18px',
                        backgroundColor: 'rgba(0,0,0,0.7)',
                        padding: '10px 20px',
                        borderRadius: '4px',
                    }}>
                    Searching...
                </Typography>
            </Box>
        );
    };

    if (tableType === 'admin-search') {
        return (
            <Box>
                {useObsidianLayout ? (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                md: sortBy !== 'none' ? '2fr 1fr 1fr' : '2fr 1fr',
                            },
                            gap: 2,
                            mb: 3,
                            width: '100%',
                        }}>
                        <ObsidianDashboardSearchField
                            placeholder="Search users, names, or emails…"
                            onChange={handleSearch}
                        />
                        <ObsidianDashboardFilterSelect value={sortBy} onChange={handleSortChange} displayEmpty>
                            <ObsidianFilterMenuItem value="none">Sort by</ObsidianFilterMenuItem>
                            <ObsidianFilterMenuItem value="fullName">Full Name</ObsidianFilterMenuItem>
                            <ObsidianFilterMenuItem value="userName">Username</ObsidianFilterMenuItem>
                            <ObsidianFilterMenuItem value="role">Role</ObsidianFilterMenuItem>
                            <ObsidianFilterMenuItem value="last_signed_in">Last Seen</ObsidianFilterMenuItem>
                            <ObsidianFilterMenuItem value="active">Status</ObsidianFilterMenuItem>
                        </ObsidianDashboardFilterSelect>
                        {sortBy !== 'none' && (
                            <ObsidianDashboardFilterSelect
                                value={sort}
                                onChange={v => handleSortDirectionChange(Number(v) as -1 | 1)}>
                                <ObsidianFilterMenuItem value={1}>Ascending</ObsidianFilterMenuItem>
                                <ObsidianFilterMenuItem value={-1}>Descending</ObsidianFilterMenuItem>
                            </ObsidianDashboardFilterSelect>
                        )}
                    </Box>
                ) : (
                    <Box
                        sx={{
                            height: '100%',
                            width: '100%',
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: 2,
                            alignItems: { xs: 'stretch', sm: 'center' },
                            mb: 2,
                        }}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                gap: 2,
                                alignItems: { xs: 'stretch', sm: 'center' },
                                flex: 1,
                                width: { xs: '100%', sm: 'auto' },
                            }}>
                            {/* Search Input */}
                            <TextField
                                variant="outlined"
                                placeholder="Search Users ..."
                                onChange={e => handleSearch(e.target.value)}
                                sx={{
                                    width: { xs: '100%', sm: 280 },
                                    '& .MuiOutlinedInput-root': {
                                        height: 42,
                                        backgroundColor: theme[themeStyle].gray_text,
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.REGULAR_HEADER,
                                        fontSize: 14,
                                        '& fieldset': {
                                            borderColor: theme[themeStyle].placeholderGray,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme[themeStyle].tag_white,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme[themeStyle].tag_white,
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        color: theme[themeStyle].tag_white,
                                        '&::placeholder': {
                                            color: theme[themeStyle].placeholderGray,
                                            opacity: 1,
                                        },
                                    },
                                }}
                            />
                            {/* Sort Dropdown */}
                            <FormControl
                                sx={{
                                    minWidth: { xs: '100%', sm: 180 },
                                    width: { xs: '100%', sm: 'auto' },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme[themeStyle].placeholderGray,
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme[themeStyle].tag_white,
                                    },
                                    '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme[themeStyle].tag_white,
                                    },
                                }}>
                                <Select
                                    variant="outlined"
                                    value={sortBy}
                                    onChange={e => handleSortChange(e.target.value)}
                                    displayEmpty
                                    sx={{
                                        height: 42,
                                        backgroundColor: theme[themeStyle].gray_text,
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.REGULAR_HEADER,
                                        fontSize: 14,
                                        '& .MuiSvgIcon-root': {
                                            color: theme[themeStyle].tag_white,
                                        },
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                bgcolor: theme[themeStyle].gray_text,
                                                '& .MuiMenuItem-root': {
                                                    color: theme[themeStyle].tag_white,
                                                    fontFamily: FONT_ENUM.REGULAR_HEADER,
                                                    fontSize: 14,
                                                    '&:hover': {
                                                        backgroundColor: theme[themeStyle].role_hover,
                                                    },
                                                    '&.Mui-selected': {
                                                        backgroundColor: theme[themeStyle].row_select,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].role_hover,
                                                        },
                                                    },
                                                },
                                            },
                                        },
                                    }}>
                                    <MenuItem value="none">None</MenuItem>
                                    <MenuItem value="fullName">Full Name</MenuItem>
                                    <MenuItem value="userName">Username</MenuItem>
                                    <MenuItem value="role">Role</MenuItem>
                                    <MenuItem value="last_signed_in">Last Seen</MenuItem>
                                    <MenuItem value="active">Status</MenuItem>
                                </Select>
                            </FormControl>
                            {/* Sort Direction Dropdown */}
                            {sortBy !== 'none' && (
                                <FormControl
                                    sx={{
                                        minWidth: { xs: '100%', sm: 140 },
                                        width: { xs: '100%', sm: 'auto' },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].placeholderGray,
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].tag_white,
                                        },
                                        '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].tag_white,
                                        },
                                    }}>
                                    <Select
                                        variant="outlined"
                                        value={sort}
                                        onChange={e => handleSortDirectionChange(Number(e.target.value) as -1 | 1)}
                                        sx={{
                                            height: 42,
                                            backgroundColor: theme[themeStyle].gray_text,
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.REGULAR_HEADER,
                                            fontSize: 14,
                                            '& .MuiSvgIcon-root': {
                                                color: theme[themeStyle].tag_white,
                                            },
                                        }}
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    bgcolor: theme[themeStyle].gray_text,
                                                    '& .MuiMenuItem-root': {
                                                        color: theme[themeStyle].tag_white,
                                                        fontFamily: FONT_ENUM.REGULAR_HEADER,
                                                        fontSize: 14,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].role_hover,
                                                        },
                                                        '&.Mui-selected': {
                                                            backgroundColor: theme[themeStyle].row_select,
                                                            '&:hover': {
                                                                backgroundColor: theme[themeStyle].role_hover,
                                                            },
                                                        },
                                                    },
                                                },
                                            },
                                        }}>
                                        <MenuItem value={1}>Ascending</MenuItem>
                                        <MenuItem value={-1}>Descending</MenuItem>
                                    </Select>
                                </FormControl>
                            )}
                        </Box>
                    </Box>
                )}
                <Box
                    sx={{
                        position: 'relative',
                        ...((useGlassLayout || useObsidianLayout) && sx && typeof sx === 'object'
                            ? (sx as object)
                            : {}),
                    }}>
                    <SearchingOverlay />
                    {useGlassLayout || useObsidianLayout ? (
                        renderDashboardListBody()
                    ) : (
                        <DataGrid
                            sx={[
                                {
                                    width: '100%',
                                    height: 'calc(100% - 2 * var(--header-height) - 2 * var(--footer-height))',
                                    '& .MuiDataGrid-filler': {
                                        backgroundColor: 'black',
                                    },
                                    '& .MuiDataGrid-columnHeader': {
                                        backgroundColor: theme[themeStyle].table_header,
                                    },
                                    '& .MuiDataGrid-row.Mui-selected': {
                                        backgroundColor: theme[themeStyle].row_select,
                                    },
                                    '&  .MuiDataGrid-row.Mui-selected:hover': {
                                        backgroundColor: theme[themeStyle].role_hover,
                                    },
                                    '&  .MuiDataGrid-row:hover': {
                                        backgroundColor: theme[themeStyle].role_hover,
                                    },
                                    '& .MuiDataGrid-columnHeaderTitleContainerContent': {
                                        color: theme[themeStyle].primary_white,
                                    },
                                    '& .MuiDataGrid-footerContainer': {
                                        display: 'none',
                                    },
                                    '& .MuiDataGrid-sortIcon': {
                                        color: theme[themeStyle].primary_white,
                                    },
                                    '& .MuiDataGrid-cell:focus': {
                                        outline: 0,
                                        outlineOffset: 0,
                                    },
                                    '& .MuiDataGrid-columnSeparator': {
                                        width: '1.5px',
                                        marginRight: '2px',
                                        color: theme[themeStyle].grey,
                                    },
                                    '& .MuiDataGrid-columnSeparator:hover': {
                                        width: '1.5px',
                                        color: theme[themeStyle].grey,
                                    },
                                    '& .MuiDataGrid-overlayWrapperInner': {
                                        backgroundColor:
                                            !isEmpty(staticData) || !isEmpty(filteredData)
                                                ? theme[themeStyle].table_row
                                                : theme[themeStyle].zeus_dark,
                                    },
                                    backgroundColor: theme[themeStyle].table_row,
                                    borderColor: theme[themeStyle].placeholderGray,
                                    borderWidth: '0.5px',
                                    borderRadius: '9px',
                                    overflowX: 'scroll',
                                    opacity: isSearchingResults ? 0.4 : 1,
                                    transition: 'opacity 0.2s ease-in-out',
                                    minHeight: 500,
                                },
                                ...(textSx != null && typeof textSx === 'object' && !Array.isArray(textSx)
                                    ? [{ '& .MuiTypography-root': textSx }]
                                    : []),
                                ...(sx != null ? (Array.isArray(sx) ? sx : [sx]) : []),
                            ]}
                            rowHeight={rowHeight}
                            columnHeaderHeight={headerHeight}
                            apiRef={apiRef}
                            slots={{
                                noResultsOverlay: NoResultsFound,
                                loadingOverlay: LoadingOverlay,
                                noRowsOverlay: NoRowsOverlay,
                                ...slots,
                            }}
                            loading={isTableLoading}
                            rows={!staticData ? filteredData : staticData}
                            columns={tableColumns}
                            getRowId={row => row._id}
                            disableColumnMenu
                        />
                    )}
                </Box>
                {renderDashboardPagination()}
            </Box>
        );
    }

    if (paginateOnly) {
        return (
            <CustomPagination
                pagination={{
                    page: page,
                    rows: rowsPerPage,
                    totalCount: count,
                }}
                rowsPerPageOptions={rowsPerPageOptions}
                onPageChange={handlePageChange}
                onRowsChange={handleRowsChange}
            />
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            {customTableButton && (
                <Box
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        width: '100%',
                    }}>
                    {customTableButton}
                </Box>
            )}
            <Box
                sx={{
                    position: 'relative',
                    ...((useGlassLayout || useObsidianLayout) && sx && typeof sx === 'object' ? (sx as object) : {}),
                }}>
                <SearchingOverlay />
                {useGlassLayout || useObsidianLayout ? (
                    renderDashboardListBody()
                ) : (
                    <DataGrid
                        sx={[
                            {
                                width: '100%',
                                height: 'calc(100% - 2 * var(--header-height) - 2 * var(--footer-height))',
                                '& .MuiDataGrid-filler': {
                                    backgroundColor: theme[themeStyle].gray_text,
                                },
                                '& .MuiDataGrid-columnHeader': {
                                    backgroundColor: theme[themeStyle].secondaryBg,
                                },
                                '& .MuiDataGrid-row.Mui-selected': {
                                    backgroundColor: theme[themeStyle].row_select,
                                },
                                '&  .MuiDataGrid-row.Mui-selected:hover': {
                                    backgroundColor: theme[themeStyle].role_hover,
                                },
                                '&  .MuiDataGrid-row:hover': {
                                    backgroundColor: theme[themeStyle].role_hover,
                                },
                                '& .MuiDataGrid-columnHeaderTitleContainerContent': {
                                    color: theme[themeStyle].primary_white,
                                },
                                '& .MuiDataGrid-footerContainer': {
                                    display: 'none',
                                },
                                '& .MuiDataGrid-sortIcon': {
                                    color: theme[themeStyle].primary_white,
                                },
                                '& .MuiDataGrid-cell:focus': {
                                    outline: 0,
                                    outlineOffset: 0,
                                },
                                '& .MuiDataGrid-columnSeparator': {
                                    width: '1.5px',
                                    marginRight: '2px',
                                    color: theme[themeStyle].grey,
                                },
                                '& .MuiDataGrid-columnSeparator:hover': {
                                    width: '1.5px',
                                    color: theme[themeStyle].grey,
                                },
                                '& .MuiDataGrid-overlayWrapperInner': {
                                    backgroundColor:
                                        !isEmpty(staticData) || !isEmpty(filteredData)
                                            ? theme[themeStyle].secondaryBg
                                            : theme[themeStyle].secondaryBg,
                                },
                                backgroundColor: theme[themeStyle].secondaryBg,
                                borderColor: theme[themeStyle].placeholderGray,
                                borderWidth: '0.5px',
                                borderRadius: '5px',
                                overflowX: 'scroll',
                                opacity: isSearchingResults ? 0.4 : 1,
                                transition: 'opacity 0.2s ease-in-out',
                            },
                            ...(textSx != null && typeof textSx === 'object' && !Array.isArray(textSx)
                                ? [{ '& .MuiTypography-root': textSx }]
                                : []),
                            ...(sx != null ? (Array.isArray(sx) ? sx : [sx]) : []),
                        ]}
                        rowHeight={rowHeight}
                        columnHeaderHeight={headerHeight}
                        apiRef={apiRef}
                        slots={{
                            noResultsOverlay: NoResultsFound,
                            loadingOverlay: LoadingOverlay,
                            noRowsOverlay: NoRowsOverlay,
                            ...slots,
                        }}
                        loading={isTableLoading}
                        rows={!staticData ? filteredData : staticData}
                        columns={tableColumns}
                        getRowId={row => row._id}
                        disableColumnMenu
                    />
                )}
            </Box>
            {renderDashboardPagination()}
        </Box>
    );
};
